import { Outlet, useMatchRoute, useParams } from "@tanstack/react-router";
import { Calendar, Mail, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { getUser, getUsersApi, listHotels, type MembershipSummary } from "@praesto/core";
import {
  AvatarOrInitials,
  StatusBadge,
  RoleBadge,
  Tabs,
  SkeletonHero,
  EmptyState,
  PageFull,
} from "@praesto/ui-kit";

type RoleLit = "tenant_owner" | "manager_hotel" | "manager_functional" | "staff";
type StatusLit = "active" | "pending" | "disabled";

interface FullPerson {
  id: string;
  displayName: string;
  email: string;
  status: StatusLit;
  phone?: string | undefined;
  photoUrl?: string | undefined;
  employment?: { startDate?: string | undefined; hoursPerWeek?: number | undefined } | undefined;
  memberships: ReadonlyArray<MembershipSummary>;
}

export function ProfileView() {
  const { tenantSlug, uid } = useParams({ strict: false }) as {
    tenantSlug: string;
    uid: string;
  };
  const matchRoute = useMatchRoute();
  const [person, setPerson] = useState<FullPerson | null>(null);
  const [hotelNames, setHotelNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      // listHotels is manager-gated; staff viewing their own profile gets
      // 403. We treat it as optional — the hotel-name map falls back to
      // the raw hotelId when the list isn't reachable.
      const [result, hotelsResult] = await Promise.all([
        getUser(api.apiBase, uid, tok),
        listHotels(api.apiBase, tok).catch(() => ({ hotels: [] })),
      ]);
      if (cancelled) return;
      const nameMap: Record<string, string> = {};
      for (const h of hotelsResult.hotels) {
        nameMap[h.id] = h.name;
      }
      setHotelNames(nameMap);
      const raw = result.user as FullPerson & { status: string };
      const status: StatusLit =
        raw.status === "active" || raw.status === "pending" || raw.status === "disabled"
          ? raw.status
          : "disabled";
      setPerson({
        id: result.user.id,
        displayName: result.user.displayName,
        email: result.user.email,
        status,
        phone: raw.phone,
        photoUrl: raw.photoUrl,
        employment: raw.employment,
        memberships: result.memberships,
      });
    })()
      .catch((e) => {
        if (!cancelled) setError(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <SkeletonHero />
      </div>
    );
  }
  if (error ?? !person) {
    return (
      <div className="p-4 sm:p-6">
        <EmptyState
          title="Persona non trovata"
          {...(error != null ? { description: error } : {})}
        />
      </div>
    );
  }

  const tabs: Array<{ path: string; label: string }> = [
    { path: "/info", label: "Info" },
    { path: "/skills", label: "Competenze" },
    { path: "/availability", label: "Disponibilità" },
    { path: "/leaves", label: "Ferie" },
    { path: "/workload", label: "Carichi di lavoro" },
  ];

  const primaryMembership = person.memberships[0];
  const primaryRole: RoleLit = primaryMembership?.role ?? "staff";
  const hotelLabel = primaryMembership
    ? (hotelNames[primaryMembership.hotelId] ?? primaryMembership.hotelId)
    : "";

  return (
    <div>
      {/* v18: the gradient Night hero is retired — the profile header is a
          plain surface on the canvas; badges and muted ink carry the facts. */}
      <header className="border-b border-border-subtle px-4 sm:px-8 pt-5 pb-4">
        <div className="flex items-center gap-4">
          <AvatarOrInitials displayName={person.displayName} photoUrl={person.photoUrl} size={48} />
          <div className="flex-1 min-w-0">
            <h1 className="m-0 text-h4 font-semibold text-fg-default truncate">
              {person.displayName}
            </h1>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <RoleBadge role={primaryRole} />
              {hotelLabel && (
                <span className="text-caption text-fg-muted truncate max-w-[60vw]">
                  {hotelLabel}
                </span>
              )}
              <StatusBadge status={person.status} />
            </div>
            <div className="mt-1.5 flex gap-4 flex-wrap min-w-0 text-caption text-fg-muted">
              {person.phone && (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-mono">
                  <Phone size={13} aria-hidden />
                  {person.phone}
                </span>
              )}
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <Mail size={13} aria-hidden className="shrink-0" />
                <span className="truncate">{person.email}</span>
              </span>
              {person.employment?.startDate && (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <Calendar size={13} aria-hidden />
                  dal <span className="font-mono">{person.employment.startDate}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </header>
      <div className="px-4 sm:px-8">
        <Tabs
          variant="underline"
          active={
            tabs.find((t) => matchRoute({ to: `/${tenantSlug}/people/${uid}${t.path}` as never }))
              ?.path ?? "/info"
          }
          items={tabs.map((t) => ({
            id: t.path,
            label: t.label,
            href: `/${tenantSlug}/people/${uid}${t.path}`,
          }))}
        />
      </div>
      <PageFull>
        <Outlet />
      </PageFull>
    </div>
  );
}
